/**
 * MessagesPage - Full page view for Message Centre
 * 
 * Provides comprehensive view of all support threads with conversation history.
 * Ideal for audit reviews and longer conversations.
 */

import { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  MessageSquare,
  Plus,
  Send,
  Clock,
  CheckCircle2,
  Loader2,
  Paperclip,
  X,
  Search,
  Filter,
  FileText,
  Image as ImageIcon,
  Upload,
  AlertCircle,
  XCircle
} from 'lucide-react';

const CATEGORIES = [
  { value: 'general_support', label: 'General Support', color: 'bg-blue-100 text-blue-700' },
  { value: 'technical_issue', label: 'Technical Issue', color: 'bg-red-100 text-red-700' },
  { value: 'billing_query', label: 'Billing Query', color: 'bg-amber-100 text-amber-700' },
  { value: 'feature_request', label: 'Feature Request', color: 'bg-purple-100 text-purple-700' }
];

const MessagesPage = () => {
  const { currentOrg, userData } = useOutletContext();
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);

  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [showNewThread, setShowNewThread] = useState(false);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // New thread form
  const [newThread, setNewThread] = useState({
    subject: '',
    category: 'general_support',
    message: ''
  });

  // Reply form
  const [replyMessage, setReplyMessage] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Fetch threads
  useEffect(() => {
    if (currentOrg?.id) {
      fetchThreads();

      // Set up realtime subscription
      const channel = supabase
        .channel('support_messages_page')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'support_messages'
          },
          (payload) => {
            if (selectedThread?.id === payload.new.thread_id) {
              fetchMessages(selectedThread.id);
            }
            fetchThreads();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [currentOrg?.id, selectedThread?.id]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchThreads = async () => {
    if (!currentOrg?.id) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('support_threads')
        .select(`
          *,
          support_messages(id, is_read, sender_type, created_at, body)
        `)
        .eq('organisation_id', currentOrg.id)
        .order('last_message_at', { ascending: false });

      if (error) throw error;

      const threadsWithUnread = (data || []).map(thread => ({
        ...thread,
        unread_count: thread.support_messages?.filter(m => m.sender_type === 'admin' && !m.is_read).length || 0,
        last_message_preview: thread.support_messages?.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]?.body || ''
      }));

      setThreads(threadsWithUnread);
    } catch (error) {
      console.error('Error fetching threads:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (threadId) => {
    try {
      setLoadingMessages(true);
      const { data, error } = await supabase
        .from('support_messages')
        .select(`
          *,
          sender:users(id, name, email)
        `)
        .eq('thread_id', threadId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setMessages(data || []);

      // Mark admin messages as read
      const unreadAdminMsgs = (data || []).filter(m => m.sender_type === 'admin' && !m.is_read);
      if (unreadAdminMsgs.length > 0) {
        await supabase
          .from('support_messages')
          .update({ is_read: true })
          .in('id', unreadAdminMsgs.map(m => m.id));
        fetchThreads();
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSelectThread = async (thread) => {
    setSelectedThread(thread);
    setShowNewThread(false);
    await fetchMessages(thread.id);
  };

  const handleCreateThread = async () => {
    if (!newThread.subject.trim() || !newThread.message.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      setSending(true);

      if (!userData?.id || !currentOrg?.id) {
        toast.error('Session error');
        return;
      }

      const { data: thread, error: threadError } = await supabase
        .from('support_threads')
        .insert({
          organisation_id: currentOrg.id,
          subject: newThread.subject,
          category: newThread.category,
          created_by: userData?.id
        })
        .select()
        .single();

      if (threadError) throw threadError;

      const { error: messageError } = await supabase
        .from('support_messages')
        .insert({
          thread_id: thread.id,
          sender_id: userData?.id,
          sender_type: 'user',
          body: newThread.message,
          attachment_urls: attachments
        });

      if (messageError) throw messageError;

      toast.success('Message sent! We\'ll respond within 24 hours.');
      setNewThread({ subject: '', category: 'general_support', message: '' });
      setAttachments([]);
      setShowNewThread(false);
      await fetchThreads();
      handleSelectThread(thread);
    } catch (error) {
      console.error('Error creating thread:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim() && attachments.length === 0) {
      toast.error('Please enter a message');
      return;
    }

    try {
      setSending(true);

      if (!userData?.id) {
        toast.error('Session error');
        return;
      }

      const { error } = await supabase
        .from('support_messages')
        .insert({
          thread_id: selectedThread.id,
          sender_id: userData?.id,
          sender_type: 'user',
          body: replyMessage,
          attachment_urls: attachments
        });

      if (error) throw error;

      if (selectedThread.status === 'closed') {
        await supabase
          .from('support_threads')
          .update({ status: 'open' })
          .eq('id', selectedThread.id);
      }

      toast.success('Reply sent!');
      setReplyMessage('');
      setAttachments([]);
      await fetchMessages(selectedThread.id);
      await fetchThreads();
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const handleCloseThread = async () => {
    if (!selectedThread) return;

    try {
      await supabase
        .from('support_threads')
        .update({ status: 'closed' })
        .eq('id', selectedThread.id);

      setSelectedThread(prev => ({ ...prev, status: 'closed' }));
      toast.success('Thread closed');
      fetchThreads();
    } catch (error) {
      toast.error('Failed to close thread');
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const maxSize = 10 * 1024 * 1024;
    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];

    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name}: Only PDF, PNG, and JPG files are allowed`);
        continue;
      }
      if (file.size > maxSize) {
        toast.error(`${file.name}: File size must be under 10MB`);
        continue;
      }

      try {
        setUploading(true);
        const fileExt = file.name.split('.').pop();
        const fileName = `${currentOrg.id}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from('message-attachments')
          .upload(fileName, file);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('message-attachments')
          .getPublicUrl(fileName);

        setAttachments(prev => [...prev, {
          name: file.name,
          url: publicUrl,
          type: file.type
        }]);

        toast.success(`${file.name} uploaded`);
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(`Failed to upload ${file.name}`);
      } finally {
        setUploading(false);
      }
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const getCategoryInfo = (category) => {
    return CATEGORIES.find(c => c.value === category) || CATEGORIES[0];
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatShortDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;

    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;

    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
  };

  // Filter threads
  const filteredThreads = threads.filter(thread => {
    if (statusFilter !== 'all' && thread.status !== statusFilter) return false;
    if (categoryFilter !== 'all' && thread.category !== categoryFilter) return false;
    if (searchTerm && !thread.subject.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  if (!currentOrg) {
    return (
      <div className="min-h-[calc(100vh-56px)] bg-[#F8F9FA] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#003B5C]" />
      </div>
    );
  }

  return (
    <div className="bg-[#F8F9FA] min-h-[calc(100vh-56px)]">
      {/* Page Header */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#003B5C] to-[#005580] rounded-xl flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-[#003B5C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Message Centre
              </h1>
              <p className="text-sm text-slate-500">Secure communication with Lucent Sky Support</p>
            </div>
          </div>
          <Button
            onClick={() => { setShowNewThread(true); setSelectedThread(null); }}
            className="bg-[#74A57F] hover:bg-[#5F8575]"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Message
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 pb-8">
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" style={{ height: 'calc(100vh - 200px)' }}>
          <div className="grid grid-cols-1 md:grid-cols-3 h-full">
            {/* Thread List */}
            <div className="border-r border-slate-200 flex flex-col h-full">
              {/* Filters */}
              <div className="p-3 border-b border-slate-200 space-y-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search messages..."
                    className="pl-9 h-9 text-sm"
                  />
                </div>
                <div className="flex space-x-2">
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="h-8 text-xs flex-1">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                    <SelectTrigger className="h-8 text-xs flex-1">
                      <SelectValue placeholder="Category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Thread List */}
              <div className="flex-1 overflow-y-auto">
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : filteredThreads.length === 0 ? (
                  <div className="p-8 text-center">
                    <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No messages found</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {filteredThreads.map((thread) => (
                      <button
                        key={thread.id}
                        onClick={() => handleSelectThread(thread)}
                        className={`w-full p-4 text-left hover:bg-slate-50 transition-colors ${selectedThread?.id === thread.id ? 'bg-blue-50 border-l-2 border-[#003B5C]' : ''
                          }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <h4 className="font-medium text-slate-800 truncate text-sm">
                                {thread.subject}
                              </h4>
                              {thread.unread_count > 0 && (
                                <span className="bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
                                  {thread.unread_count}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-slate-500 truncate mt-1">
                              {thread.last_message_preview?.substring(0, 60)}...
                            </p>
                            <div className="flex items-center space-x-2 mt-2">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${getCategoryInfo(thread.category).color}`}>
                                {getCategoryInfo(thread.category).label}
                              </span>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${thread.status === 'open' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'
                                }`}>
                                {thread.status === 'open' ? 'Open' : 'Closed'}
                              </span>
                            </div>
                          </div>
                          <span className="text-xs text-slate-400 ml-2 shrink-0">
                            {formatShortDate(thread.last_message_at)}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Conversation Panel */}
            <div className="col-span-2 flex flex-col h-full">
              {showNewThread ? (
                /* New Thread Form */
                <div className="flex-1 overflow-y-auto p-6">
                  <h3 className="font-semibold text-lg text-[#003B5C] mb-6">New Message</h3>

                  <div className="space-y-4 max-w-xl">
                    <div>
                      <Label>Category</Label>
                      <Select
                        value={newThread.category}
                        onValueChange={(val) => setNewThread(prev => ({ ...prev, category: val }))}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CATEGORIES.map(cat => (
                            <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Subject</Label>
                      <Input
                        value={newThread.subject}
                        onChange={(e) => setNewThread(prev => ({ ...prev, subject: e.target.value }))}
                        placeholder="Brief summary of your enquiry"
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label>Message</Label>
                      <Textarea
                        value={newThread.message}
                        onChange={(e) => setNewThread(prev => ({ ...prev, message: e.target.value }))}
                        placeholder="Describe your enquiry in detail..."
                        className="mt-1 resize-none"
                        rows={8}
                      />
                    </div>

                    <div>
                      <Label>Attachments (optional)</Label>
                      <div className="mt-1">
                        {attachments.length > 0 && (
                          <div className="mb-2 flex flex-wrap gap-2">
                            {attachments.map((att, idx) => (
                              <div key={idx} className="flex items-center bg-slate-100 rounded px-2 py-1 text-xs">
                                <span className="truncate max-w-[150px]">{att.name}</span>
                                <button onClick={() => removeAttachment(idx)} className="ml-1 text-slate-400 hover:text-red-500">
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".pdf,.png,.jpg,.jpeg"
                          multiple
                          className="hidden"
                          onChange={handleFileUpload}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploading}
                        >
                          {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                          Upload Files
                        </Button>
                        <p className="text-xs text-slate-400 mt-1">PDF, PNG, JPG up to 10MB each</p>
                      </div>
                    </div>

                    <div className="flex space-x-3 pt-4">
                      <Button
                        onClick={handleCreateThread}
                        disabled={sending || !newThread.subject.trim() || !newThread.message.trim()}
                        className="bg-[#003B5C] hover:bg-[#002A42]"
                      >
                        {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                        Send Message
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setShowNewThread(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              ) : selectedThread ? (
                /* Conversation View */
                <>
                  {/* Thread Header */}
                  <div className="p-4 border-b border-slate-200 bg-slate-50">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-lg text-[#003B5C]">{selectedThread.subject}</h3>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${getCategoryInfo(selectedThread.category).color}`}>
                            {getCategoryInfo(selectedThread.category).label}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${selectedThread.status === 'open' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'
                            }`}>
                            {selectedThread.status === 'open' ? 'Open' : 'Closed'}
                          </span>
                          <span className="text-xs text-slate-400">
                            Created {formatDate(selectedThread.created_at)}
                          </span>
                        </div>
                      </div>
                      {selectedThread.status === 'open' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleCloseThread}
                          className="text-slate-600"
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Close Thread
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {loadingMessages ? (
                      <div className="flex justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                      </div>
                    ) : (
                      <>
                        {messages.map((msg) => (
                          <div
                            key={msg.id}
                            className={`flex ${msg.sender_type === 'user' ? 'justify-end' : 'justify-start'}`}
                          >
                            <div className={`max-w-[70%] rounded-lg p-4 ${msg.sender_type === 'user'
                                ? 'bg-[#003B5C] text-white'
                                : 'bg-slate-100 text-slate-800'
                              }`}>
                              <p className="whitespace-pre-wrap">{msg.body}</p>

                              {msg.attachment_urls && msg.attachment_urls.length > 0 && (
                                <div className="mt-3 space-y-1 pt-2 border-t border-white/20">
                                  {msg.attachment_urls.map((att, idx) => (
                                    <a
                                      key={idx}
                                      href={att.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className={`flex items-center text-sm ${msg.sender_type === 'user' ? 'text-white/80 hover:text-white' : 'text-blue-600 hover:underline'
                                        }`}
                                    >
                                      {att.type?.includes('image') ? <ImageIcon className="w-4 h-4 mr-2" /> : <FileText className="w-4 h-4 mr-2" />}
                                      {att.name}
                                    </a>
                                  ))}
                                </div>
                              )}

                              <p className={`text-xs mt-2 ${msg.sender_type === 'user' ? 'text-white/60' : 'text-slate-400'}`}>
                                {msg.sender_type === 'admin' ? 'Lucent Sky Support' : (msg.sender?.name || 'You')} • {formatDate(msg.created_at)}
                              </p>
                            </div>
                          </div>
                        ))}
                        <div ref={messagesEndRef} />
                      </>
                    )}
                  </div>

                  {/* Reply Input */}
                  <div className="p-4 border-t border-slate-200 bg-white">
                    {attachments.length > 0 && (
                      <div className="mb-3 flex flex-wrap gap-2">
                        {attachments.map((att, idx) => (
                          <div key={idx} className="flex items-center bg-slate-100 rounded px-2 py-1 text-xs">
                            <span className="truncate max-w-[150px]">{att.name}</span>
                            <button onClick={() => removeAttachment(idx)} className="ml-1 text-slate-400 hover:text-red-500">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="flex items-end space-x-3">
                      <div className="flex-1">
                        <Textarea
                          value={replyMessage}
                          onChange={(e) => setReplyMessage(e.target.value)}
                          placeholder="Type your reply..."
                          className="resize-none"
                          rows={3}
                        />
                      </div>
                      <div className="flex space-x-2">
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".pdf,.png,.jpg,.jpeg"
                          multiple
                          className="hidden"
                          onChange={handleFileUpload}
                        />
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploading}
                        >
                          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
                        </Button>
                        <Button
                          onClick={handleSendReply}
                          disabled={sending || (!replyMessage.trim() && attachments.length === 0)}
                          className="bg-[#003B5C] hover:bg-[#002A42]"
                        >
                          {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                          Send
                        </Button>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                /* Empty State */
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <MessageSquare className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="font-medium text-slate-700 mb-2">Select a conversation</h3>
                    <p className="text-slate-500 text-sm">Choose a thread from the list or start a new message</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessagesPage;
